import './wdyr'; // just that line, don't touch anything else from that file
import './index.scss';
import './i18n';

import * as Sentry from '@sentry/react';

import { ConvergeSnackBarProvider, applicationTheme } from '@converge/ui';
import { config, convergeLogger } from '@converge/config';
import markerSDK, { MarkerSdk } from '@marker.io/browser';

import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { ApolloProvider } from '@apollo/client';
import App from './app-comp';

import AuthProvider from '@converge/ui/domain/auth/auth-provider';
import { BrowserTracing } from '@sentry/tracing';
import { useConvergeApolloClient } from './converge-apollo-client';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import ReactDOM from 'react-dom/client';
import { StrictMode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import reportWebVitals from './report-web-vitals';
import { LicenseInfo } from '@mui/x-license-pro';

LicenseInfo.setLicenseKey(config.muiKey);

convergeLogger.info(
  `${config.debug ? '[DEBUG MODE] ' : ''}Converge Frontend ${
    config.appVersion
  } (${config.commitHash}) is starting in ${config.convergeEnv} mode...`
);

if (config.enableSentry) {
  convergeLogger.log('Sentry is enabled');
}

Sentry.init({
  dsn: 'https://e0948be7491348c7a5b65133e49aa238@o1255381.ingest.sentry.io/6423897',
  integrations: [new BrowserTracing()],
  enabled: config.enableSentry,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  environment: config.convergeEnv.toString(),
  release: config.appVersionWithCommitHash,
  ignoreErrors: config.errorsToIgnore,
});

if (config.enableDataDog) {
  convergeLogger.log(`DataDog is enabled and targeting ${config.dataDogSite}`);
  datadogRum.init({
    applicationId: config.dataDogApplicationId ?? '',
    clientToken: config.dataDogClientToken ?? '',
    env: config.convergeEnv,
    service: config.dataDogService,
    sessionReplaySampleRate: 100,
    sessionSampleRate: 100,
    site: config.dataDogSite,
    trackFrustrations: true,
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    version: config.appVersionWithCommitHash,
  });

  datadogLogs.init({
    clientToken: config.dataDogClientToken ?? '',
    env: config.convergeEnv,
    forwardConsoleLogs: 'all',
    forwardErrorsToLogs: true,
    forwardReports: 'all',
    service: config.dataDogService,
    site: config.dataDogSite,
    trackLongTasks: true,
    trackResources: true,
    version: config.appVersionWithCommitHash,
  });
}

let MARKER_IO_SDK: MarkerSdk;

if (config.enableMarkerIo) {
  convergeLogger.log('Marker.io is enabled');

  markerSDK
    .loadWidget({
      project: config.markerIoProjectId,
      source: 'snippet',
      customData: {
        appVersion: config.appVersionWithCommitHash,
        environment: config.convergeEnv,
      },
    })
    .then((sdk) => (MARKER_IO_SDK = sdk));
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const ConvergeApolloClient = () => {
  const convergeApolloClient = useConvergeApolloClient();

  return (
    <StrictMode>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <AuthProvider
          onUserChange={(user) => {
            if (config.enableMarkerIo && MARKER_IO_SDK) {
              if (user && user.user.username) {
                MARKER_IO_SDK.setReporter({
                  email: user.user.username,
                  fullName: user.user.username.replace(/@.*$/, ''),
                });
              } else {
                // TODO: Wait until Marker.io gets back to this: https://github.com/marker-io/browser-sdk/issues/26
                convergeLogger.warn(
                  `Marker.io does not yet support clearing the reporter.`
                );
              }
            }
          }}
        >
          <ApolloProvider client={convergeApolloClient}>
            <ThemeProvider theme={applicationTheme}>
              <ConvergeSnackBarProvider>
                <App />
              </ConvergeSnackBarProvider>
            </ThemeProvider>
          </ApolloProvider>
        </AuthProvider>
      </LocalizationProvider>
    </StrictMode>
  );
};
root.render(<ConvergeApolloClient />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
